import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { Sparkles, ShieldCheck, Zap } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1552664730-d307ca884978?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

export default function Login() {
  const { login, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("abqaridr@gmail.com");
  const [password, setPassword] = useState("admin123");
  const [busy, setBusy] = useState(false);
  const [otpStage, setOtpStage] = useState(null); // { challenge_id, email_hint }
  const [otp, setOtp] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email, password);
      if (data && data.requires_otp) {
        setOtpStage({ challenge_id: data.challenge_id, email_hint: data.email_hint });
        toast.info("Kode verifikasi dikirim ke email Anda");
      } else {
        toast.success("Selamat datang kembali");
        navigate("/");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) return toast.error("Kode harus 6 digit");
    setBusy(true);
    try {
      await verifyOtp(otpStage.challenge_id, otp);
      toast.success("Verifikasi berhasil");
      navigate("/");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#151A18]">
      {/* Background photo */}
      <div className="absolute inset-0">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover"
             style={{ filter: "blur(6px) saturate(1.05)", transform: "scale(1.05)" }}/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(21,26,24,0.72) 0%, rgba(45,71,57,0.60) 60%, rgba(21,26,24,0.85) 100%)" }}/>
        {/* Grain */}
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')" }}/>
      </div>

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-2">
        {/* Left: brand + narrative */}
        <div className="hidden lg:flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-[#D99C3B] flex items-center justify-center font-display font-bold text-[#151A18] rounded-sm shadow-lg">A</div>
            <div>
              <div className="font-display text-xl tracking-tight">Adika Cipta Kreasindo</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-0.5">Finance · Project</div>
            </div>
          </div>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-[0.25em] uppercase bg-white/10 backdrop-blur-md border border-white/20 mb-6">
              <Sparkles size={12} className="text-[#D99C3B]"/> Satu Ruang, Semua Angka
            </div>
            <h1 className="font-display text-5xl leading-[1.05] tracking-tight mb-4">
              Kelola keuangan &<br/>project dengan tenang.
            </h1>
            <p className="text-white/70 leading-relaxed max-w-sm">Invoice, purchase order, surat jalan, stok, aset dan project management — semua siap dari desktop maupun mobile.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <div className="flex items-center gap-2 text-xs text-white/70"><ShieldCheck size={14} className="text-[#4A6B53]"/> Data terisolasi per akun</div>
              <div className="flex items-center gap-2 text-xs text-white/70"><Zap size={14} className="text-[#D99C3B]"/> AI insight otomatis</div>
            </div>
          </div>

          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">© 2026 Adika Cipta Kreasindo · Made in Indonesia</div>
        </div>

        {/* Right: glass login card */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <form onSubmit={otpStage ? submitOtp : submit} data-testid="login-form"
                className="w-full max-w-sm rounded-2xl p-8 sm:p-10 backdrop-blur-2xl bg-white/10 border border-white/20 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.6)]">
            {/* Mobile brand */}
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-9 h-9 bg-[#D99C3B] flex items-center justify-center font-display font-bold text-[#151A18] rounded-sm">A</div>
              <div className="font-display text-lg text-white">Adika Cipta Kreasindo</div>
            </div>

            {otpStage ? (
              <>
                <div className="mb-6">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2">Verifikasi 2 Langkah</div>
                  <h2 className="font-display text-3xl text-white tracking-tight">Cek email Anda.</h2>
                  <p className="text-sm text-white/60 mt-2">Kode 6 digit dikirim ke <span className="text-[#D99C3B]">{otpStage.email_hint}</span>. Berlaku 10 menit.</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.2em] text-white/70">Kode OTP</Label>
                  <Input data-testid="otp-code" value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} required
                         inputMode="numeric" placeholder="000000" maxLength={6}
                         className="mt-1.5 rounded-lg h-14 bg-white/5 border-white/20 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#D99C3B]"/>
                </div>
                <Button disabled={busy} data-testid="otp-submit"
                        className="w-full mt-6 h-12 rounded-lg bg-[#D99C3B] hover:bg-[#c48a2f] text-[#151A18] font-medium shadow-lg shadow-[#D99C3B]/20">
                  {busy ? "Memverifikasi…" : "Verifikasi & Masuk"}
                </Button>
                <button type="button" onClick={()=>{setOtpStage(null); setOtp("");}} className="w-full mt-3 text-xs text-white/60 hover:text-white">
                  ← Kembali & coba email lain
                </button>
              </>
            ) : (
              <>
            <div className="mb-8">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60 mb-2">Masuk</div>
              <h2 className="font-display text-3xl text-white tracking-tight">Selamat datang<br/>kembali.</h2>
              <p className="text-sm text-white/60 mt-2">Lanjutkan pekerjaan Anda di Adika Cipta Kreasindo.</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/70">Email</Label>
                <Input data-testid="login-email" value={email} onChange={(e)=>setEmail(e.target.value)} required type="email"
                       className="mt-1.5 rounded-lg h-11 bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-[#D99C3B] focus-visible:border-transparent" />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-white/60">
                  <Link to="/forgot-password" data-testid="link-forgot" className="text-[#D99C3B] hover:text-[#F5B84A] underline underline-offset-4">Lupa password?</Link>
                </div>
                <span className="text-[10px] text-white/40">min 6 karakter</span>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-white/70">Password</Label>
                <Input data-testid="login-password" value={password} onChange={(e)=>setPassword(e.target.value)} required type="password"
                       className="mt-1.5 rounded-lg h-11 bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-[#D99C3B] focus-visible:border-transparent" />
              </div>
            </div>

            <Button disabled={busy} data-testid="login-submit"
                    className="w-full mt-8 h-12 rounded-lg bg-[#D99C3B] hover:bg-[#c48a2f] text-[#151A18] font-medium transition-colors shadow-lg shadow-[#D99C3B]/20">
              {busy ? "Memproses…" : "Masuk ke Dashboard"}
            </Button>

            <div className="text-sm text-white/60 mt-6 text-center">
              Belum punya akun? <Link to="/register" className="text-[#D99C3B] hover:text-[#F5B84A] font-medium underline underline-offset-4">Daftar sekarang</Link>
            </div>
              </>
            )}
          </form>
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
